import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useLocationContext } from "../../context/LocationContext";
import { useDispatch, useSelector } from "react-redux";

import { FaMapMarkerAlt, FaHeart, FaRegHeart, FaStar } from "react-icons/fa";
import { Search } from "lucide-react";

import {
  fetchLabDetails,
  fetchLabTests,
  fetchPackages,
  fetchLabDistance,
} from "../../services/LabTestApi/LabDetails";

import { useCartActions } from "../../hooks/useCartActions";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";

// ✅ Import rating slice
import { fetchAllRatings } from "../../Redux/ratingSlice";

import PackagesSection from "./PackagesSection";
import TestDetails from "./TestDetails";
import LoginPopup from "../common/LoginPopup";

export default function LabDetails() {
  const { id } = useParams();
  const labId = Number(id);
  const dispatch = useDispatch();

  const location = useLocation();
  const stateLab = location.state?.lab;

  const from = (location.state?.from || "")
    .toString()
    .trim()
    .toLowerCase();

  const { cart = [] } = useCart();
  const { coords } = useLocationContext();

  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  // ✅ Ratings state
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);

  const {
    addWithReplaceCheck,
    removeItem,
    LoginPopupComponent,
  } = useCartActions();

  const [lab, setLab] = useState(null);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distance, setDistance] = useState(null);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const testsPerPage = 14;

  const [testSearch, setTestSearch] = useState("");

  // ================= RATING HELPER =================
  const getTestRating = (testId) => {
    const key = `${testId}_2`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const renderStars = (rating = 0) => {
    const fullStars = Math.floor(rating);
    return [...Array(5)].map((_, i) =>
      i < fullStars ? <FaStar key={i} size={12} color="#ffc107" /> : <FaStar key={i} size={12} color="#ddd" />
    );
  };

  // ================= WISHLIST =================
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      return userData?.userId || null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  };

  const isInWishlist = (productId, categoryId = 2) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) return false;
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const toggleWishlist = async (test, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = test?.testID || test?.id;
    const categoryId = 2;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ================= API LOAD =================
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const labData = await fetchLabDetails(labId);
        setLab(labData || stateLab);

        const t = await fetchLabTests({ labId });
        setTests(t || []);

        const p = await fetchPackages(labId);
        setPackages(p);

        await loadWishlist();

        // ✅ Fetch ratings for all tests
        if (t.length > 0) {
          const ratingProducts = t.map((test) => {
            const id = test?.testID || test?.id;
            return { productId: id, categoryId: 2 };
          });
          await dispatch(fetchAllRatings(ratingProducts));
        }

      } catch (e) {
        console.log("❌ API ERROR:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [labId]);

  // Fetch distance
  useEffect(() => {
    const getDistance = async () => {
      if (!lab || !coords) return;
      try {
        const distanceData = await fetchLabDistance({
          labId: labId,
          userLat: coords.lat,
          userLng: coords.lng,
        });
        
        if (distanceData && distanceData.length > 0) {
          const distanceValue = distanceData[0]?.labValue || distanceData[0]?.Distance;
          const numericDistance = parseFloat(distanceValue);
          setDistance(isNaN(numericDistance) ? null : numericDistance);
        } else {
          setDistance(null);
        }
      } catch (error) {
        console.error("❌ Distance API error:", error);
        setDistance(null);
      }
    };

    getDistance();
  }, [lab, coords, labId]);

  // SEARCH FILTER
  const filteredTests = tests.filter((t) =>
    t?.testName?.toLowerCase().includes(testSearch.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTests.length / testsPerPage);

  const paginatedTests = filteredTests.slice(
    (currentPage - 1) * testsPerPage,
    currentPage * testsPerPage
  );

  const hasDistance = distance !== null;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isFromPackages = from === "packages";
  const showTests = !isFromPackages;

  return (
    <div className="w-full bg-gray-50 min-h-screen pb-20">

      {/* HEADER */}
      <div className="bg-white p-4 shadow-md rounded-xl mx-3 flex gap-4 items-center mt-5">
        <img
          src={lab?.image || "https://placehold.co/400x250"}
          alt="lab"
          className="h-[110px] w-[140px] object-contain rounded-lg border bg-gray-50"
        />
        <div className="flex-1">
          <h2 className="text-xl font-bold">
            {lab?.lab_name || lab?.name}
          </h2>
          {lab?.description && (
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              {lab.description}
            </p>
          )}
          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
            <FaMapMarkerAlt className="text-red-500" />
            {lab?.address}
          </p>
          {hasDistance && (
            <p className="text-green-600 text-xs mt-1">
              🚗 {distance} km away
            </p>
          )}
        </div>
      </div>

      {!hasDistance ? (
        <div className="text-center mt-10">Calculating distance...</div>
      ) : (
        <>
          {showTests && (
            <div className="p-3 mt-5">

              <h4 className="text-center text-2xl font-bold mb-4 text-red-600">
                Available Tests
              </h4>

              {/* SEARCH BAR */}
              <div className="flex justify-center mb-5">
                <div className="relative w-full md:w-[400px]">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search test..."
                    value={testSearch}
                    onChange={(e) => {
                      setTestSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-full border shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {paginatedTests.map((t) => {
                  const testId = t?.testID || t?.id;
                  const inCart = cart.some(
                    (item) => Number(item.productId) === Number(testId)
                  );
                  const inWishlist = isInWishlist(testId, 2);
                  
                  const originalPrice = Number(t?.testPrice || t?.TestPrice) || 0;
                  const sellingPrice = Number(t?.sellingPrice || t?.SellingPrice) || originalPrice;
                  const discount = originalPrice - sellingPrice;
                  const hasDiscount = discount > 0;
                  const discountPercent = originalPrice > 0 ? Math.round((discount / originalPrice) * 100) : 0;
                  const img = t?.testImage;

                  // ✅ Get rating
                  const { average: avgRating, total: totalReviews } = getTestRating(testId);

                  return (
                    <div
                      key={testId}
                      onClick={() => setSelectedTestId(testId)}
                      className="bg-white border rounded-xl shadow-sm p-3 flex flex-col w-full cursor-pointer hover:shadow-md transition hover:-translate-y-1 duration-300 relative"
                    >
                      {/* Wishlist Button */}
                      <button
                        onClick={(e) => toggleWishlist(t, e)}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                        disabled={wishlistLoading}
                      >
                        {wishlistLoading ? (
                          <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          inWishlist ? (
                            <FaHeart size={14} className="text-red-500 fill-red-500" />
                          ) : (
                            <FaRegHeart size={14} className="text-gray-400 hover:text-red-500" />
                          )
                        )}
                      </button>

                      {/* Discount Badge */}
                      {hasDiscount && discount > 0 && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                            {discountPercent}% OFF
                          </div>
                        </div>
                      )}

                      {/* IMAGE */}
                      <div className="h-[110px] w-full flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden mt-6">
                        <img
                          src={img}
                          alt="test"
                          className="max-h-full max-w-full object-contain p-2"
                          onError={(e) =>
                            (e.target.src = "https://placehold.co/60x60?text=Test")
                          }
                        />
                      </div>

                      {/* NAME */}
                      <p className="text-[12px] mt-2 line-clamp-2 min-h-[32px] font-semibold text-gray-700 text-center">
                        {t?.testName}
                      </p>

                      {/* ✅ RATINGS UI - exactly like PopularTests */}
                      <div className="flex justify-center items-center gap-1 mt-1">
                        <div className="flex gap-0.5">
                          {renderStars(avgRating || 0)}
                        </div>
                        {!ratingLoading && totalReviews > 0 && (
                          <span className="text-[9px] text-gray-400">({totalReviews})</span>
                        )}
                      </div>

                      {/* PRICE + BUTTON */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-bold text-green-600">
                              ₹{sellingPrice}
                            </span>
                            {originalPrice > 0 && (
                              <span className={`text-[10px] ${hasDiscount ? 'line-through text-gray-400' : 'text-gray-400'}`}>
                                ₹{originalPrice}
                              </span>
                            )}
                          </div>
                          {hasDiscount && discount > 0 && (
                            <span className="text-[9px] text-red-500 font-semibold">
                              Save ₹{discount}
                            </span>
                          )}
                          {!hasDiscount && discount === 0 && originalPrice > 0 && (
                            <span className="text-[9px] text-gray-400">No Discount</span>
                          )}
                          {originalPrice === 0 && sellingPrice === 0 && (
                            <span className="text-[9px] text-gray-400">Price not available</span>
                          )}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            inCart
                              ? removeItem({
                                  productId: testId,
                                  categoryId: 2,
                                  price: sellingPrice,
                                  storeId: labId,
                                  productName: t?.testName,
                                })
                              : addWithReplaceCheck({
                                  productId: testId,
                                  categoryId: 2,
                                  price: sellingPrice,
                                  storeId: labId,
                                  productName: t?.testName,
                                });
                          }}
                          className={`flex items-center justify-center gap-1 px-3 py-1.5 min-w-[75px] rounded-md text-[11px] font-semibold border
                            ${
                              inCart
                                ? "bg-red-50 text-red-600 border-red-500 hover:bg-red-100"
                                : "bg-blue-50 text-blue-600 border-blue-500 hover:bg-blue-100"
                            }`}
                        >
                          {inCart ? "Remove" : "Add"}
                        </button>
                      </div>

                      <p className="text-[10px] text-gray-400 truncate mt-1 text-center">
                        {t?.labName || lab?.lab_name}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-6 gap-2 flex-wrap items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                    className={`px-3 py-1.5 text-sm rounded-md border transition
                      ${
                        currentPage === 1
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)]
                    .map((_, index) => index + 1)
                    .filter((page) => {
                      return (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      );
                    })
                    .map((page, index, arr) => {
                      const prevPage = arr[index - 1];

                      return (
                        <React.Fragment key={page}>
                          {prevPage && page - prevPage > 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}

                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition
                              ${
                                currentPage === page
                                  ? "bg-red-500 text-white"
                                  : "bg-white text-gray-600 hover:bg-gray-100"
                              }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className={`px-3 py-1.5 text-sm rounded-md border transition
                      ${
                        currentPage === totalPages
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    Next
                  </button>
                </div>
              )}

              {totalPages > 1 && (
                <div className="text-center text-xs text-gray-400 mt-3">
                  Showing {paginatedTests.length > 0 ? (currentPage - 1) * testsPerPage + 1 : 0} - {Math.min(currentPage * testsPerPage, filteredTests.length)} of {filteredTests.length} tests
                </div>
              )}
            </div>
          )}

          <div className="px-3 mt-6">
            <PackagesSection labId={labId} />
          </div>
        </>
      )}

      {selectedTestId && (
        <TestDetails
          id={selectedTestId}
          onClose={() => setSelectedTestId(null)}
        />
      )}

      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
      />

      {LoginPopupComponent}
    </div>
  );
}